import { useEffect, useState } from 'react';

/**
 * Created in the effect rather than a memo, so each mount owns the URLs it revokes: a memo creates them
 * once while cleanup runs on every unmount, so StrictMode's remount left every `<img>` on a dead blob.
 *
 * Rendered from blob URLs, never the remote URL. An `<img>` without `crossorigin` sends no `Origin`, and
 * the HTTP cache is not keyed by request mode, so that cached response is later served to `getFile`'s
 * CORS fetch and throws — breaking every subsequent share.
 *
 * Shared by both arms. `previews` must be referentially stable — a fresh array each render re-mints the
 * URLs and revokes the ones the `<img>` tags still point at.
 *
 * @see https://fetch.spec.whatwg.org/#cors-protocol-and-http-caches
 */
export function usePreviewUrls(previews: readonly File[]): readonly string[] {
	const [urls, setUrls] = useState<readonly string[]>([]);

	useEffect(() => {
		const created = previews.map((preview) => URL.createObjectURL(preview));
		// eslint-disable-next-line react-hooks/set-state-in-effect -- the URL API is unavailable on the server, so these cannot be created during render.
		setUrls(created);

		return () => {
			created.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [previews]);

	return urls;
}
